import type AbstractModel from '@model-core/core/AbstractModel';
import { listPage } from '@/models/listPage';

/**
 * Résumé d'une ressource (TimeSheetSummary, TeamSummary, PeriodSummary) : ressource à part entière, comme sur Igienair
 * (listBy(ids, 'channel') puis findBy par la page) ; le parent ne le porte pas, il le déclare en modèle lié
 * (@doc.model) que fetchSummaries remplit.
 */
export interface SummarySpec {
  /** Modèle du résumé */
  model: ModelConstructor;
  /** Relation du résumé vers son parent, qui sert aussi de filtre (timeSheet[]=…) */
  parent: string;
  /** Modèle lié du parent qui reçoit le résumé (timeSheetSummary) */
  property: string;
}

/**
 * Résumés de modèles déjà chargés, en une requête (GET /time_sheet_summaries?timeSheet[]=…&partial=true : une seule
 * requête SQL côté API, sans comptage), rattachés à leur parent comme model-core remplit une relation
 * (setAttributes). Un résumé refusé ou en erreur laisse les compteurs du parent à 0 : la liste s'affiche quand même.
 */
export async function fetchSummaries<M extends AbstractModel>(models: M[], spec: SummarySpec) {
  const ids = [...new Set(models.map((model) => model.id).filter(Boolean))];
  if (!ids.length) return;
  try {
    const { models: summaries } = await listPage(spec.model, {
      filters: { [spec.parent]: ids as (string | number)[] },
      page: 1,
      itemsPerPage: ids.length,
      partial: true,
    });
    // Relation vers le parent (résumé) ou modèle lié du résumé (parent) : un @doc.model, nommé par la spec
    const related = (model: object, name: string) => (model as Record<string, AbstractModel>)[name];
    const byParent = new Map(summaries.map((summary) => [String(related(summary, spec.parent).id), summary]));
    for (const model of models) {
      const summary = byParent.get(String(model.id));
      if (summary) related(model, spec.property).setAttributes(summary.attributes);
    }
  } catch {
    // résumés non lisibles : compteurs à 0
  }
}
